/**
 * "Talk to Hero" voice widget — client-side VAPI configuration.
 *
 * GitHub issue #555, root-cause investigation item #5: "The
 * VoiceAssistant component has hardcoded: VAPI_PUBLIC_KEY / ASSISTANT_ID
 * / WS Server ... Are these still valid?" No such component existed
 * anywhere in this repo before this fix (see `docs/flows/02-voice-widget.md`),
 * so there was nothing to un-hardcode — but the ticket's own framing
 * ("a rotated ngrok free-tier URL baked into the client bundle") is
 * exactly the anti-pattern this module exists to avoid for the *new*
 * implementation: every value below is read from `import.meta.env.VITE_*`
 * (per this run's rule #7 — never `process.env` in browser code), never
 * a literal string in this file, so a rotated tunnel URL or a rotated
 * VAPI key is a config/redeploy change, not a code change.
 *
 * The VAPI public key and assistant ID are intentionally client-visible
 * by design (same trust model as a Stripe *publishable* key) — see the
 * security test case this satisfies: "No private API keys or the
 * WebSocket server credentials are exposed ... beyond the intended
 * public VAPI key". `apps/workers/src/api/widget.ts`'s
 * `validateWidgetEmail` never returns these values (or anything else) —
 * they live only in this build-time config, never round-tripped through
 * an API response.
 */

export interface VapiWidgetConfig {
  /** VAPI's public (publishable-key-equivalent) key. Null if unconfigured. */
  publicKey: string | null;
  /** VAPI assistant id to start a conversation against. Null if unconfigured. */
  assistantId: string | null;
  /** Voice WebSocket server URL (e.g. an ngrok tunnel in dev, a stable
   * domain in production). Null if unconfigured. */
  wsUrl: string | null;
}

/** Reads a `import.meta.env` value, returning `null` for unset/blank
 * rather than an empty string (so callers can `if (!config.wsUrl)` once). */
function readEnv(envVars: Record<string, string | undefined>, key: string): string | null {
  const value = envVars[key];
  return value && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Resolves the VAPI widget config from Vite's `import.meta.env`.
 *
 * @param envOverrides - Optional explicit env map, for tests (mirrors
 *   `@/lib/env.ts`'s `loadEnvironmentConfig(env, envOverrides)` pattern).
 */
export function getVapiWidgetConfig(
  envOverrides?: Record<string, string | undefined>
): VapiWidgetConfig {
  const envVars = envOverrides ?? (import.meta.env as unknown as Record<string, string | undefined>);

  return {
    publicKey: readEnv(envVars, 'VITE_VAPI_PUBLIC_KEY'),
    assistantId: readEnv(envVars, 'VITE_VAPI_ASSISTANT_ID'),
    wsUrl: readEnv(envVars, 'VITE_VOICE_WS_URL'),
  };
}

/** True only when every field needed to actually start a voice call is present. */
export function isVapiWidgetConfigured(config: VapiWidgetConfig): boolean {
  return Boolean(config.publicKey && config.assistantId && config.wsUrl);
}
